import React, { useState, useEffect } from 'react';
import { colors, fonts, spacing, radii, layout } from '../styles/theme';
import { fetchFeedbackStats } from '../services/api';
import Header from './Header';

interface LandingPageProps {
    onBegin: () => void;
    onOpenVisualizer?: () => void;
    onOpenFeedbackViewer?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onBegin, onOpenVisualizer, onOpenFeedbackViewer }) => {
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        fetchFeedbackStats()
            .then(({ stats }) => setUnreadCount(stats?.unread || 0))
            .catch(() => {});
    }, []);

    return (
        <div style={styles.page}>
            <Header cartCount={0} showCart={false} />
            <main style={styles.main}>
                <section style={styles.heroSection}>
                    <div style={styles.heroRow}>
                        <div style={styles.heroText}>
                            <h1 style={styles.heroTitle}>The Shopping Bag Tool for VR Ethics and Legal issues</h1>
                            <p style={styles.heroSubtitle}>
                                A structured self-assessment for ethical and legal considerations in VR projects.
                            </p>
                            <div style={styles.heroActions}>
                                <button style={styles.primaryButton} onClick={onBegin}>
                                    Get started
                                </button>
                                <button
                                    style={styles.feedbackHeroButton}
                                    onClick={() => {
                                        window.location.search = '?feedback';
                                    }}
                                >
                                    Give feedback
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <div style={styles.blogColumn}>
                    <section style={styles.contentSection}>
                        <h2 style={styles.heading}>What this tool is and what it does</h2>
                        <p style={styles.paragraph}>
                            The Shopping Bag Tool is a structured reflection support for teams thinking about or already implementing VR in professional settings.
                            It guides participants through a sequence of ethical and legal questions and helps identify topics that
                            require further review.
                        </p>
                        <p style={styles.paragraph}>
                            During the session, each topic can be marked as under control, not relevant, or added to the bag for follow-up.
                            At the end, the tool provides a summary and a downloadable report that can be used for planning,
                            documentation, and continued work.
                        </p>
                    </section>

                    <section style={styles.contentSection}>
                        <div style={styles.twoColumn}>
                            <div style={styles.column}>
                                <h2 style={styles.heading}>Who we are</h2>
                                <p style={styles.paragraph}>
                                    The tool was built by researchers at RISE, <strong>Laurynas Adomaitis</strong> (ethics) and <strong>Emmie Nordell</strong> (legal). The Shopping Bag Tool was co-designed with
                                    researchers and strategists from <strong>Regional Forensic Psychiatric Clinic in Växjö and Kronoberg Region</strong>.
                                </p>
                            </div>
                            <div style={styles.column}>
                                <h2 style={styles.heading}>Privacy and data</h2>
                                <p style={styles.paragraph}>
                                    <strong>No data is collected, stored, or tracked</strong> when you use this tool – not even for analytics.
                                    Everything happens locally in your browser.
                                </p>
                                <p style={styles.paragraph}>
                                    At the end, you can download a plain text summary or take your own notes. Nothing is sent to us.
                                </p>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Author cards at bottom */}
                <section style={styles.contactSection}>
                    <h2 style={styles.contactHeading}>Any questions or feedback? Get in touch with us.</h2>
                    <div style={styles.contactCardsRow}>
                        <div style={styles.personCard}>
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/image.png`}
                                alt="Laurynas Adomaitis"
                                style={styles.personImage}
                            />
                            <h3 style={styles.personName}>Laurynas Adomaitis</h3>
                            <p style={styles.personRole}>Ethics researcher, RISE</p>
                            <div style={styles.personLinks}>
                                <a
                                    href="https://www.linkedin.com/in/laurynas-adomaitis/"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.linkButton}
                                >
                                    LinkedIn
                                </a>
                                <a
                                    href="mailto:laurynas.adomaitis@ri.se?subject=Shopping%20Bag%20Tool"
                                    style={styles.linkSecondaryButton}
                                >
                                    Email
                                </a>
                            </div>
                        </div>
                        <div style={styles.personCard}>
                            <img
                                src={`${process.env.PUBLIC_URL || ''}/image%20copy.png`}
                                alt="Emmie Nordell"
                                style={styles.personImage}
                            />
                            <h3 style={styles.personName}>Emmie Nordell</h3>
                            <p style={styles.personRole}>Legal researcher, RISE</p>
                            <div style={styles.personLinks}>
                                <a
                                    href="https://www.linkedin.com/in/emmie-nordell-16154b60/"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={styles.linkButton}
                                >
                                    LinkedIn
                                </a>
                                <a
                                    href="mailto:emmie.nordell@ri.se?subject=Shopping%20Bag%20Tool"
                                    style={styles.linkSecondaryButton}
                                >
                                    Email
                                </a>
                            </div>
                        </div>
                    </div>
                {/* Vinnova funding disclaimer */}
                <div style={styles.disclaimer}>
                    <img
                        src={`${process.env.PUBLIC_URL || ''}/vinnova-logo.svg`}
                        alt="Vinnova"
                        style={styles.vinnovaLogo}
                    />
                    <p style={styles.disclaimerText}>
                        Funded under <em>"Legal and ethical analysis and operational guidelines for Virtual Reality"</em> by Vinnova.
                        Reference number 2025-00489. Coordinator: RISE Research Institutes of Sweden AB.
                    </p>
                </div>

                {(onOpenVisualizer || onOpenFeedbackViewer) && (
                    <div style={styles.adminControlRow}>
                            <span style={styles.adminControlNote}>
                                (For Emmie and Laurynas only)
                            </span>
                            {onOpenVisualizer && (
                                <button
                                    type="button"
                                    style={styles.adminControlButton}
                                    onClick={onOpenVisualizer}
                                >
                                    Edit indicators (control panel)
                                </button>
                            )}
                            {onOpenFeedbackViewer && (
                                <button
                                    type="button"
                                    style={{
                                        ...styles.adminControlButton,
                                        ...(unreadCount > 0 ? {
                                            borderColor: colors.primary,
                                            color: colors.primary,
                                        } : {}),
                                    }}
                                    onClick={onOpenFeedbackViewer}
                                >
                                    View feedback
                                    {unreadCount > 0 && (
                                        <span style={styles.feedbackBadge}>{unreadCount}</span>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: '100vh',
        backgroundColor: colors.bgLight,
        display: 'flex',
        flexDirection: 'column',
    },
    main: {
        flex: 1,
        padding: `${spacing[8]} ${spacing[6]}`,
        maxWidth: layout.maxWidth,
        margin: '0 auto',
    },
    heroSection: {
        marginBottom: spacing[8],
    },
    heroRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing[8],
        flexWrap: 'wrap',
    },
    heroText: {
        flex: 1,
        maxWidth: 640,
    },
    heroActions: {
        display: 'flex',
        gap: spacing[3],
        alignItems: 'center',
        flexWrap: 'wrap' as const,
    },
    heroTitle: {
        fontSize: fonts['3xl'],
        fontWeight: fonts.bold,
        color: colors.text,
        marginBottom: spacing[3],
    },
    heroSubtitle: {
        fontSize: fonts.lg,
        color: colors.textSecondary,
        marginBottom: spacing[5],
        maxWidth: 640,
    },
    contactSection: {
        marginBottom: spacing[8],
    },
    contactHeading: {
        fontSize: fonts.base,
        fontWeight: fonts.medium,
        color: colors.text,
        marginBottom: spacing[1],
    },
    personCard: {
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        padding: spacing[4],
        boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
    },
    personImage: {
        width: '100%',
        height: 160,
        display: 'block',
        borderRadius: radii.md,
        objectFit: 'cover',
        marginBottom: spacing[3],
    },
    personName: {
        fontSize: fonts.base,
        fontWeight: fonts.semibold,
        color: colors.text,
        marginBottom: spacing[1],
    },
    personRole: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        marginBottom: spacing[3],
    },
    personLinks: {
        display: 'flex',
        gap: spacing[2],
        flexWrap: 'wrap',
    },
    linkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.white,
        backgroundColor: colors.primary,
        borderRadius: radii.full,
        textDecoration: 'none',
    },
    linkSecondaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.textSecondary,
        backgroundColor: colors.bgLight,
        borderRadius: radii.full,
        textDecoration: 'none',
    },
    contactCardsRow: {
        display: 'flex',
        gap: spacing[4],
        flexWrap: 'wrap',
    },
    blogColumn: {
        width: '100%',
    },
    primaryButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[6]}`,
        fontSize: fonts.base,
        fontWeight: fonts.semibold,
        color: colors.white,
        backgroundColor: colors.primary,
        borderRadius: radii.md,
        border: 'none',
        cursor: 'pointer',
    },
    visualizerLink: {
        display: 'inline-block',
        marginLeft: spacing[4],
        padding: `${spacing[2]} ${spacing[3]}`,
        fontSize: fonts.sm,
        color: colors.textSecondary,
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
    },
    adminControlRow: {
        marginTop: spacing[4],
        display: 'flex',
        alignItems: 'center',
        gap: spacing[3],
        flexWrap: 'wrap',
    },
    adminControlNote: {
        fontSize: fonts.xs,
        color: colors.no,
        fontWeight: fonts.semibold,
    },
    adminControlButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[3]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.textSecondary,
        backgroundColor: colors.bgWarm,
        borderRadius: radii.full,
        border: `1px solid ${colors.border}`,
        cursor: 'pointer',
        gap: spacing[1],
    },
    feedbackBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        fontSize: '0.65rem',
        fontWeight: fonts.bold,
        color: colors.white,
        backgroundColor: colors.primary,
        borderRadius: radii.full,
    },
    contentSection: {
        marginBottom: spacing[8],
    },
    twoColumn: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: spacing[8],
    },
    column: {
        fontSize: fonts.base,
        color: colors.textSecondary,
    },
    heading: {
        fontSize: fonts.xl,
        fontWeight: fonts.semibold,
        color: colors.text,
        marginBottom: spacing[3],
    },
    subheading: {
        fontSize: fonts.lg,
        fontWeight: fonts.semibold,
        color: colors.text,
        marginBottom: spacing[3],
    },
    paragraph: {
        fontSize: fonts.base,
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
        marginBottom: spacing[3],
    },
    list: {
        paddingLeft: spacing[5],
        marginTop: 0,
        marginBottom: spacing[3],
        fontSize: fonts.base,
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
    },
    listItem: {
        marginBottom: spacing[2],
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: spacing[4],
    },
    phaseCard: {
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
        padding: spacing[4],
    },
    phaseTitle: {
        fontSize: fonts.base,
        fontWeight: fonts.semibold,
        color: colors.text,
        marginBottom: spacing[2],
    },
    feedbackHeroButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[5]}`,
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.textSecondary,
        backgroundColor: 'transparent',
        borderRadius: radii.md,
        border: `1px solid ${colors.border}`,
        cursor: 'pointer',
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        marginTop: spacing[6],
        padding: spacing[4],
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
    },
    vinnovaLogo: {
        width: '80px',
        height: 'auto',
        flexShrink: 0,
    },
    disclaimerText: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
    },
};

export default LandingPage;
